import request from '@/utils/request'
import { getCommunityId, getCommunityName } from '@/api/community/communityApi'
import { getUuid } from '@/utils/commonUtil'
import { getDateYYYYMMDDHHMISS } from '@/utils/dateUtil'

/**
 * 解压模板内容（兼容新旧格式）
 * @param {string} urlOrContent - 数据库中的url字段值
 * @returns {object} { type: 'route'|'html', content: string }
 * 
 * 返回示例:
 * - 旧格式: { type: 'route', content: '/#/pages/property/printPayFee' }
 * - 新格式: { type: 'html', content: '<!DOCTYPE html>...' }
 */
export function decompressTemplate(urlOrContent) {
  if (!urlOrContent) return { type: 'route', content: '' }
  
  // 检查是否是压缩的HTML内容（新格式）
  if (urlOrContent.startsWith('data:html;base64,')) {
    try {
      const base64Content = urlOrContent.replace('data:html;base64,', '')
      // 使用浏览器原生 atob 解码 Base64（无需第三方依赖）
      const htmlContent = decodeURIComponent(escape(atob(base64Content)))
      console.log('【模板解压成功】新格式')
      console.log('解压后HTML长度:', htmlContent ? htmlContent.length : 0, '字节')
      return { type: 'html', content: htmlContent || '' }
    } catch (error) {
      console.error('【模板解压失败】', error)
      return { type: 'html', content: '' }
    }
  }
  
  // 旧格式：Vue路由路径
  console.log('【模板读取】旧格式 - 路由路径')
  return { type: 'route', content: urlOrContent }
}

/**
 * 智能打印收据（支持新旧格式）
 * @param {string} printUrl - 数据库中的url字段值
 * @param {object} params - URL参数对象
 * @param {string} params.receiptId - 收据ID（可选）
 * @param {string} params.detailIds - 明细IDs（可选）
 * @param {string} params.merge - 是否合并（可选）
 * @param {string} params.receiptIds - 收据IDs（可选）
 * 
 * 使用示例:
 * smartPrintReceipt(printUrl, { receiptId: '123' })
 * smartPrintReceipt(printUrl, { detailIds: '456', merge: 'Y' })
 */
export function smartPrintReceipt(printUrl, params = {}) {
  if (!printUrl) {
    console.error('【打印失败】printUrl为空')
    return
  }
  
  const result = decompressTemplate(printUrl)
  
  // 构建URL参数字符串
  const queryParams = new URLSearchParams()
  Object.keys(params).forEach(key => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      queryParams.append(key, params[key])
    }
  })
  const queryString = queryParams.toString()
  
  if (result.type === 'html') {
    // 新格式：打开新窗口并写入HTML
    const newWindow = window.open('', '_blank')
    if (!newWindow) {
      console.error('【打印失败】浏览器阻止了弹窗')
      return
    }
    
    console.log('【打印窗口】开始写入HTML内容...')
    console.log('【打印窗口】HTML长度:', result.content.length, '字节')
    console.log('【打印窗口】URL参数:', queryString)
    
    // 注入API_BASE_URL和URL_PARAMS
    let htmlContent = result.content
    
    // 正式环境：使用相对路径，让Nginx反代处理
    const finalApiUrl = '/app'
    console.log('【打印窗口】API基础URL:', finalApiUrl)
    
    // 获取token用于认证
    const token = localStorage.getItem('token') || ''
    console.log('【打印窗口】localStorage中的token:', token ? token.substring(0, 20) + '...' : '空')
    const authorization = token ? `Bearer ${token}` : ''
    console.log('【打印窗口】Authorization:', authorization ? '已设置 (长度:' + authorization.length + ')' : '未设置')
    
    // 生成事务ID和请求时间
    const transactionId = getUuid()
    const requestTime = getDateYYYYMMDDHHMISS()
    console.log('【打印窗口】TRANSACTION-ID:', transactionId)
    console.log('【打印窗口】REQ-TIME:', requestTime)
    
    // 获取communityId和communityName
    const communityId = getCommunityId()
    const communityName = getCommunityName()
    console.log('【打印窗口】communityId:', communityId)
    console.log('【打印窗口】communityName:', communityName)
    
    // 替换占位符
    htmlContent = htmlContent.replace('{__AUTHORIZATION__}', authorization)
    htmlContent = htmlContent.replace('{__TRANSACTION_ID__}', transactionId)
    htmlContent = htmlContent.replace('{__REQUEST_TIME__}', requestTime)
    htmlContent = htmlContent.replace('{__COMMUNITY_ID__}', communityId)
    htmlContent = htmlContent.replace('{__COMMUNITY_NAME__}', communityName)
    
    // 注入URL查询字符串(供模板内的JavaScript使用)
    if (htmlContent.includes('{__URL_QUERY_STRING__}')) {
      htmlContent = htmlContent.replace('{__URL_QUERY_STRING__}', queryString)
      console.log('【打印窗口】已注入URL查询字符串:', queryString)
    }
    
    // 如果有URL_PARAMS占位符，替换为实际参数
    if (htmlContent.includes('{__URL_PARAMS__}')) {
      htmlContent = htmlContent.replace('{__URL_PARAMS__}', JSON.stringify(params))
    }
    
    // 兼容旧模板：直接替换getUrlParam调用和undefined/null参数
    if (params.receiptId) {
      htmlContent = htmlContent.replace(/getUrlParam\("receiptId"\)/g, '"' + params.receiptId + '"')
      htmlContent = htmlContent.replace(/getUrlParam\('receiptId'\)/g, '"' + params.receiptId + '"')
    }
    if (params.detailIds) {
      htmlContent = htmlContent.replace(/getUrlParam\("detailIds"\)/g, '"' + params.detailIds + '"')
      htmlContent = htmlContent.replace(/getUrlParam\('detailIds'\)/g, '"' + params.detailIds + '"')
    }
    if (params.merge) {
      htmlContent = htmlContent.replace(/getUrlParam\("merge"\)/g, '"' + params.merge + '"')
      htmlContent = htmlContent.replace(/getUrlParam\('merge'\)/g, '"' + params.merge + '"')
    }
    
    console.log('【打印窗口】占位符替换完成')
    
    // 写入完整的HTML内容
    newWindow.document.open()
    newWindow.document.write(htmlContent)
    newWindow.document.close()
    
    console.log('【打印成功】新格式 - HTML模板')
  } else {
    // 旧格式：跳转到Vue路由页面
    const baseUrl = result.content
    const url = queryString ? `${baseUrl}?${queryString}` : baseUrl
    window.open(url, '_blank')
    console.log('【打印成功】旧格式 - Vue路由:', url)
  }
}

// 获取收据模板列表
export function listFeePrintPage(params) {
  return new Promise((resolve, reject) => {
    request({
      url: '/feePrintPage.listFeePrintPage',
      method: 'get',
      params: {
        ...params,
        communityId: getCommunityId()
      }
    }).then(response => {
      const res = response.data
      resolve(res)
    }).catch(error => {
      reject(error)
    })
  })
}

// 获取收据页面模板列表
export function listFeePrintPageTemplate(params) {
  return new Promise((resolve, reject) => {
    request({
      url: '/feePrintPageTemplate.listFeePrintPageTemplate',
      method: 'get',
      params: {
        ...params,
        communityId: getCommunityId()
      }
    }).then(response => {
      const res = response.data
      resolve(res)
    }).catch(error => {
      reject(error)
    })
  })
}

// 添加收据模板
export function saveFeePrintPage(data) {
  return new Promise((resolve, reject) => {
    request({
      url: '/feePrintPage.saveFeePrintPage',
      method: 'post',
      data: {
        ...data,
        communityId: getCommunityId()
      }
    }).then(response => {
      const res = response.data
      resolve(res)
    }).catch(error => {
      reject(error)
    })
  })
}

// 更新收据模板
export function updateFeePrintPage(data) {
  return new Promise((resolve, reject) => {
    request({
      url: '/feePrintPage.updateFeePrintPage',
      method: 'post',
      data: {
        ...data,
        communityId: getCommunityId()
      }
    }).then(response => {
      const res = response.data
      resolve(res)
    }).catch(error => {
      reject(error)
    })
  })
}

// 更新收据模板状态（启用/禁用）
export function updateFeePrintPageState(data) {
  return new Promise((resolve, reject) => {
    request({
      url: '/feePrintPage.updateFeePrintPage',
      method: 'post',
      data: {
        ...data,
        communityId: getCommunityId()
      }
    }).then(response => {
      const res = response.data
      resolve(res)
    }).catch(error => {
      reject(error)
    })
  })
}

// 删除收据模板
export function deleteFeePrintPage(data) {
  return new Promise((resolve, reject) => {
    request({
      url: '/feePrintPage.deleteFeePrintPage',
      method: 'post',
      data: {
        ...data,
        communityId: getCommunityId()
      }
    }).then(response => {
      const res = response.data
      resolve(res)
    }).catch(error => {
      reject(error)
    })
  })
}

// 保存收据页面模板到 fee_print_page_template 表
export function saveFeePrintPageTemplate(data) {
  return new Promise((resolve, reject) => {
    request({
      url: '/feePrintPageTemplate.saveFeePrintPageTemplate',
      method: 'post',
      data: {
        ...data,
        communityId: getCommunityId()
      }
    }).then(response => {
      const res = response.data
      resolve(res)
    }).catch(error => {
      reject(error)
    })
  })
}